import React, { useState } from "react";
import { Control, Controller, FieldErrors } from "react-hook-form";

import { FormValidationRules } from "../../utils/types/FormValidationRules";
import { getValidate } from "../../utils/validation/getValidate";
import { FailureSeverity, ValidationFailure } from "../../utils/validation/ValidationResult";
import { DropdownOption } from "../inputs/dropdown/DropdownOption";
import { MultiselectSearchDropdown } from "../inputs/dropdown/MultiselectSearchDropdown";
import { Label } from "../inputs/label/Label";
import { Stack } from "../layouts/stack/Stack";
import { FormErrorMessage } from "./FormErrorMessage";
import { FormFailureMessages } from "./FormFailureMessages";
import { getFormLabelText } from "./getFormLabelText";
import { useFormWatch } from "./useFormWatch";

export interface FormMultiselectDropdownProps {
    label: string;
    name: string;
    options: DropdownOption[];
    control: Control;
    errors: FieldErrors;
    rules?: FormMultiselectDropdownRules;
    disabled?: boolean;
    dependsOn?: string[];
    placeholder?: string;
    onChange?(values: string[]): void;
    testId?: string;
}

export const FormMultiselectDropdown: React.FunctionComponent<FormMultiselectDropdownProps> = ({
    label,
    name,
    options,
    control,
    errors,
    rules,
    disabled,
    dependsOn,
    placeholder,
    testId,
    onChange
}: FormMultiselectDropdownProps) => {
    const [warnings, setWarnings] = useState<ValidationFailure[]>([]);

    const clearWarnings = () => {
        setWarnings([]);
    };

    const handleOnChange = () => {
        if (onChange) {
            onChange([]);
            clearWarnings();
        }
    };

    if (dependsOn) {
        useFormWatch({
            name,
            control,
            dependsOn,
            onClear: handleOnChange
        });
    }

    const render = ({ value, onChange: onFormChange }: { value?: string[]; onChange(values?: string[]): void }) => {
        const handleChange = (selectedOptions?: DropdownOption[]) => {
            clearWarnings();
            const selectedValues = selectedOptions?.map((item) => String(item.key)) ?? [];
            onFormChange(selectedValues);

            if (onChange) {
                onChange(selectedValues);
            }
        };

        return (
            <Stack data-testid={testId}>
                <Label disabled={disabled}>{getFormLabelText(label, !!rules?.required)}</Label>
                <MultiselectSearchDropdown
                    options={options}
                    selectedKeys={value}
                    disabled={disabled}
                    onChange={handleChange}
                    placeholder={placeholder}
                    name={name}
                    errors={errors}
                />
            </Stack>
        );
    };

    return (
        <Stack>
            <Controller
                name={name}
                control={control}
                rules={{
                    ...rules,
                    validate: getValidate<string[], FormMultiselectDropdownRules>(
                        rules,
                        control.formStateRef.current.submitCount,
                        setWarnings
                    )
                }}
                render={render}
            />
            <FormErrorMessage name={name} errors={errors} testId={`${name}-errors`} />
            <FormFailureMessages failures={warnings} severity={FailureSeverity.Warning} testId={`${name}-warnings`} />
        </Stack>
    );
};

export type FormMultiselectDropdownRules = FormValidationRules<string[]>;
